import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { IMAGES, WA_DEFAULT } from "../../lib/site";
import { Eyebrow } from "./Reveal";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function KineticHero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <section
            id="top"
            ref={ref}
            data-testid="kinetic-hero"
            className="relative overflow-hidden border-b border-hairline bg-white px-5 pb-20 pt-32 sm:px-8 md:pt-44"
        >
            <div className="mx-auto grid max-w-6xl items-center gap-14 md:grid-cols-[1.15fr_1fr]">
                <motion.div style={{ y: textY }}>
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mb-8"
                    >
                        <Eyebrow>Studio Pilates · Lago di Como</Eyebrow>
                    </motion.div>

                    <h1
                        data-testid="hero-heading"
                        className="text-[2.9rem] font-light leading-[1.05] tracking-[-0.03em] text-ink sm:text-6xl lg:text-7xl"
                    >
                        <MaskedLine delay={0.25}>Movimento</MaskedLine>
                        <MaskedLine delay={0.38}>consapevole nel</MaskedLine>
                        <MaskedLine delay={0.51}>
                            cuore di{" "}
                            <em className="font-accent font-medium italic text-ochre">
                                Cernobbio.
                            </em>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-8 max-w-md text-base font-light leading-relaxed text-body sm:text-lg"
                    >
                        Reformer Studio in piccoli gruppi di massimo quattro
                        persone. A due passi dal lungolago, uno spazio di calma e
                        precisione dedicato al tuo benessere.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <a
                            href="#classi"
                            data-testid="hero-cta-book"
                            className="bg-ink px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-all duration-200 hover:bg-charcoal active:scale-[0.98]"
                        >
                            Prenota una lezione
                        </a>
                        <a
                            href={WA_DEFAULT}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-cta-whatsapp"
                            className="flex items-center gap-2 border border-ink px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-200 hover:bg-ink hover:text-white active:scale-[0.98]"
                        >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp
                        </a>
                    </motion.div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative"
                >
                    <div className="overflow-hidden border border-hairline">
                        <motion.img
                            style={{ y: imgY, scale: 1.15 }}
                            src={IMAGES.hero}
                            alt="La sala macchine dello studio Kore a Cernobbio, con Cadillac e Matcha Bar"
                            className="h-[26rem] w-full object-cover sm:h-[32rem]"
                            loading="eager"
                        />
                    </div>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.15 }}
                        className="absolute -bottom-7 left-5 border border-hairline bg-white/90 px-6 py-4 backdrop-blur-md"
                    >
                        <p className="text-2xl font-light tracking-tight text-ink">
                            Max 4
                        </p>
                        <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-mute">
                            persone per classe
                        </p>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
}
